import { type QuoteLine } from '@kalos/kalos-rpc';
import { Button, Card } from '@kalos/ui';
import { type FC, useCallback, useRef } from 'react';
import SignaturePad from 'react-signature-canvas';

interface ProposalQuickSignProps {
  lineItems: QuoteLine[];
  signature: string;
  onSignatureChange: (signature: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}

export const ProposalQuickSign: FC<ProposalQuickSignProps> = ({
  lineItems,
  signature,
  onSignatureChange,
  onSubmit,
  onBack,
  isSubmitting,
}) => {
  const signaturePadRef = useRef<SignaturePad>(null);

  // Calculate total
  const total = lineItems.reduce((sum, item) => {
    const price = parseFloat(item.adjustment || '0');
    const quantity = item.quotedQuantity || 1;
    return sum + price * quantity;
  }, 0);

  // Handle signature clear
  const handleSignatureClear = useCallback(() => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      onSignatureChange('');
    }
  }, [onSignatureChange]);

  // Handle signature end (when user finishes drawing)
  const handleSignatureEnd = useCallback(() => {
    if (signaturePadRef.current) {
      const signatureData = signaturePadRef.current.toDataURL('image/png', 1.0);
      onSignatureChange(signatureData);
    }
  }, [onSignatureChange]);

  return (
    <div className="space-y-6 bg-white">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-gray-900">
          Proposal Quick Update - Review & Sign
        </h2>
        <p className="text-sm text-gray-600">
          Please review the line items and provide your signature to authorize this proposal update.
        </p>
      </div>

      {/* Line Items Summary */}
      <Card className="p-6">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Line Items Summary</h3>
        <div className="space-y-3">
          {lineItems.map((item, index) => (
            <div
              key={index}
              className="flex items-center justify-between border-b pb-3 last:border-b-0"
            >
              <div className="flex-1">
                <div className="font-medium text-gray-900">{item.description}</div>
              </div>
              <div className="text-right text-gray-900">
                ${parseFloat(item.adjustment || '0').toFixed(2)} × {item.quotedQuantity || 1} = $
                {(parseFloat(item.adjustment || '0') * (item.quotedQuantity || 1)).toFixed(2)}
              </div>
            </div>
          ))}
        </div>

        {/* Total */}
        <div className="mt-4 flex justify-between border-t pt-4">
          <div className="text-lg font-bold text-gray-900">Total:</div>
          <div className="text-2xl font-bold text-gray-900">${total.toFixed(2)}</div>
        </div>
      </Card>

      {/* Signature Section */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Signature</h3>
          <Button
            type="button"
            variant="outline"
            onClick={handleSignatureClear}
            size="sm"
            className="text-gray-600 hover:text-gray-900"
          >
            Clear
          </Button>
        </div>

        <Card className="p-4">
          <div className="relative">
            <SignaturePad
              ref={signaturePadRef}
              canvasProps={{
                className:
                  'w-full h-[200px] border-2 border-dashed border-gray-300 rounded-lg bg-white',
              }}
              onEnd={handleSignatureEnd}
            />
            {!signature && (
              <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center text-sm text-gray-400">
                Sign above using your mouse or touchpad
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between border-t pt-4">
        <Button variant="outline" onClick={onBack} disabled={isSubmitting}>
          Back
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!signature || isSubmitting}
          className="bg-primary hover:bg-primary/90"
        >
          {isSubmitting ? 'Submitting...' : 'Submit Update'}
        </Button>
      </div>
    </div>
  );
};
